/**
 * Pure transforms between the agentic-config wire format (snake_case) and the
 * frontend's domain types. Keeping these functions pure (no Ky calls, no React
 * hooks) means the service module and the MSW handlers can both rely on them
 * to stay in lock-step with the backend contract.
 */

import type {
  AuditLogResponse,
  CategoryResponse,
  ParameterConstraintsWire,
  ParameterResponse,
  ParameterValueTypeWire,
} from '../types/configuration.api.types';
import type {
  AuditEvent,
  ConfigurationPlatform,
  Parameter,
  ParameterCategory,
  ParameterScope,
  ParameterSection,
  ParameterSource,
  ParameterValue,
  ParameterValueSchema,
} from '../types/configuration.types';

// ───────────────────────────────────────────────────────────────────────────
// Source / scope / section coercion
// ───────────────────────────────────────────────────────────────────────────

const KNOWN_SOURCES: ReadonlySet<ParameterSource> = new Set([
  'Analytics',
  'System',
  'PM',
  'Engineering',
]);

/**
 * Backend currently seeds `source` as the literal "Global" (mis-set in
 * `agentic-config/scripts/seed.py`). Until that's fixed we coerce unknown
 * values to `'System'` so the source-badge component doesn't crash on a
 * missing style/i18n key.
 * TODO(backend): drop this fallback once seeded sources are real team names.
 */
function toSource(raw: string | null | undefined): ParameterSource {
  if (raw && KNOWN_SOURCES.has(raw as ParameterSource)) return raw as ParameterSource;
  return 'System';
}

function toScope(raw: string): ParameterScope {
  return raw === 'Per-client' ? 'Per-client' : 'Global';
}

/** Narrows the loosely-typed wire `section` to the union. Unknown values
 *  fall back to `'experiment'` so the rail still renders the category
 *  somewhere instead of dropping it. */
function toSection(raw: string): ParameterSection {
  return raw === 'production' ? 'production' : 'experiment';
}

// ───────────────────────────────────────────────────────────────────────────
// value_type + constraints → ParameterValueSchema
// ───────────────────────────────────────────────────────────────────────────

/**
 * Builds the discriminated `ParameterValueSchema` the edit dialog reads.
 * `duration` collapses into `{ type: 'number', unit }` where `unit` is taken
 * from `constraints.unit` (e.g. "seconds", "hours", "days"). Falls back to
 * `'Days'` only when the backend omits the unit entirely. `enum` values are a
 * flat `allowed_values: string[]` on the wire — each value is used as its own
 * label since the backend doesn't ship a separate display string. Unknown
 * value_types fall through to a string schema — safer than throwing at the
 * boundary if the backend introduces a new type before the frontend updates.
 */
export function toValueSchema(
  valueType: ParameterValueTypeWire,
  constraints: ParameterConstraintsWire | null,
): ParameterValueSchema {
  switch (valueType) {
    case 'percentage':
      return {
        type: 'percentage',
        min: constraints?.min ?? 0,
        max: constraints?.max ?? 100,
      };
    case 'number':
      return {
        type: 'number',
        min: constraints?.min ?? 0,
        max: constraints?.max ?? Number.MAX_SAFE_INTEGER,
        ...(constraints?.unit ? { unit: constraints.unit } : {}),
      };
    case 'duration':
      return {
        type: 'number',
        min: constraints?.min ?? 0,
        max: constraints?.max ?? Number.MAX_SAFE_INTEGER,
        unit: constraints?.unit ?? 'Days',
      };
    case 'boolean':
      return { type: 'boolean' };
    case 'enum':
      // Backend ships a flat string list — each value doubles as its own label.
      return {
        type: 'enum',
        options: (constraints?.allowed_values ?? []).map((v) => ({ value: v, label: v })),
      };
    case 'string':
      return { type: 'string' };
    default:
      return { type: 'string' };
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Wire-string → typed ParameterValue
// ───────────────────────────────────────────────────────────────────────────

/** Parses the always-string `current_value` into the typed value the UI
 *  consumes. Falls back to the raw string for malformed numerics. */
export function parseValue(raw: string, valueType: ParameterValueTypeWire): ParameterValue {
  switch (valueType) {
    case 'percentage':
    case 'number':
    case 'duration': {
      const n = Number(raw);
      return Number.isFinite(n) ? n : raw;
    }
    case 'boolean':
      return raw === 'true';
    default:
      return raw;
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Pre-formatted display string
// ───────────────────────────────────────────────────────────────────────────

/** Computes the string rendered in the Current Value column. Mirrors the
 *  conventions previously hard-coded in MSW fixtures. */
export function formatDisplayValue(value: ParameterValue, schema: ParameterValueSchema): string {
  switch (schema.type) {
    case 'percentage':
      return `${String(value)}%`;
    case 'number':
      return schema.unit ? `${String(value)} ${schema.unit}` : String(value);
    case 'boolean':
      return value ? 'Enabled' : 'Disabled';
    case 'enum': {
      const match = schema.options.find((o) => o.value === String(value));
      return match?.label ?? String(value);
    }
    case 'string':
      return String(value);
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Top-level mappers
// ───────────────────────────────────────────────────────────────────────────

/**
 * Wire `ParameterResponse` → frontend `Parameter`. `category` carries the
 * backend `category_id` (UUID) — the human label, order, and section are
 * resolved at the page layer via the parallel `/parameters/categories` query.
 *
 * `platform` on the frontend type is the human-readable name (`'GMC'`, …)
 * not the platform UUID — callers pass it through since they know which
 * platform they queried for.
 */
export function toParameter(p: ParameterResponse, platform: ConfigurationPlatform): Parameter {
  const valueSchema = toValueSchema(p.value_type, p.constraints);
  const value = parseValue(p.current_value, p.value_type);

  return {
    id: p.id,
    platform,
    category: p.category_id,
    name: p.name,
    key: p.key,
    value,
    displayValue: formatDisplayValue(value, valueSchema),
    scope: toScope(p.scope),
    source: toSource(p.source),
    valueSchema,
    lastModifiedAt: p.last_modified_at,
    lastModifiedBy: p.last_modified_by ?? '',
    isOverride: p.is_override ?? false,
    // Falls back to `id` if the backend ever omits `definition_id` — keeps
    // history lookups working (just scoped to this row) rather than breaking.
    definitionId: p.definition_id ?? p.id,
  };
}

/** Wire `CategoryResponse` → frontend `ParameterCategory`. Narrows the
 *  loosely-typed `section` field and renames `sort_order`/`parameter_count`
 *  to camelCase for the rest of the FE. */
export function toParameterCategory(c: CategoryResponse): ParameterCategory {
  return {
    id: c.id,
    name: c.name,
    section: toSection(c.section),
    sortOrder: c.sort_order,
    parameterCount: c.parameter_count,
  };
}

/** Wire `AuditLogResponse` → frontend `AuditEvent`. */
export function toAuditEvent(a: AuditLogResponse): AuditEvent {
  return {
    id: a.id,
    parameterId: a.parameter_id,
    parameterKey: a.parameter_key,
    parameterName: a.parameter_name,
    previousValue: a.previous_value,
    newValue: a.new_value,
    scope: toScope(a.scope),
    reason: a.reason ?? '',
    changedAt: a.created_at,
    changedByUserId: a.user_id,
    changedBy: a.user_name,
  };
}
